import React from 'react';
import { FileSpreadsheet, Link2, Database, Eye, Edit2, Trash2, RefreshCw, CheckCircle2, Clock, AlertCircle, Calendar } from 'lucide-react';
import type { DataSource } from '@/types';

interface DataSourceCardProps {
  datasource: DataSource;
  onView: (datasource: DataSource) => void;
  onEdit: (datasource: DataSource) => void;
  onDelete: (datasource: DataSource) => void;
  onAnalyze: (datasourceId: string | number) => void;
  isAnalyzing: boolean;
  canManage: boolean;
}

const getTypeIcon = (type?: string) => {
  switch (type) {
    case 'GOOGLE_SHEETS':
      return { icon: FileSpreadsheet, color: 'text-sky-400', bgColor: 'bg-sky-500/20' };
    case 'API_REST':
      return { icon: Link2, color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    default:
      return { icon: Database, color: 'text-slate-400', bgColor: 'bg-slate-500/20' };
  }
};

const getTypeLabel = (type?: string) => {
  switch (type) {
    case 'GOOGLE_SHEETS': return 'Google Sheets';
    case 'API_REST': return 'API REST';
    default: return 'Desconocido';
  }
};

const formatRelativeTime = (dateString?: string) => {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  return date.toLocaleDateString('es-ES', { month: 'short', day: 'numeric' });
};

export const DataSourceCard: React.FC<DataSourceCardProps> = ({
  datasource,
  onView,
  onEdit,
  onDelete,
  onAnalyze,
  isAnalyzing,
  canManage
}) => {
  const typeInfo = getTypeIcon(datasource.type);
  const Icon = typeInfo.icon;
  
  // Extract analysis data
  const analysis = datasource.analysis as any;
  const records = analysis?.dataProfile?.rowCount ||
                  analysis?.unifiedProfile?.rowCount ||
                  analysis?.profile?.rowCount || 
                  analysis?.rowCount || 
                  analysis?.meta?.analyzedRows ||
                  0;
  
  const fields = analysis?.dataProfile?.columns?.length ||
                 analysis?.unifiedProfile?.columnCount ||
                 analysis?.unifiedProfile?.columns?.length ||
                 analysis?.profile?.columns?.length || 
                 analysis?.columns?.length || 
                 analysis?.schemaJson?.columns?.length ||
                 analysis?.smartSchema?.columns?.length ||
                 0;

  const getStatusBadge = () => {
    if (datasource.status === 'ACTIVE') {
      return (
        <div className="flex items-center gap-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span className="text-slate-300">
            {records.toLocaleString()} filas · {fields} campos
          </span>
        </div>
      );
    } else if (datasource.status === 'INACTIVE') {
      return (
        <div className="flex items-center gap-2 text-sm text-amber-400">
          <Clock className="w-4 h-4" />
          <span>Pendiente de análisis</span>
        </div>
      );
    } else if (datasource.status === 'SUSPENDED') {
      return (
        <div>
          <div className="flex items-center gap-2 text-sm text-red-400 mb-1">
            <AlertCircle className="w-4 h-4" />
            <span>Error en análisis</span>
          </div>
          {datasource.statusMessage && (
            <p className="text-xs text-red-400/80 ml-6">
              {datasource.statusMessage}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const borderColor = datasource.status === 'SUSPENDED' 
    ? 'border-red-500/50' 
    : datasource.status === 'ACTIVE'
    ? 'border-slate-700 hover:border-sky-500/50'
    : 'border-slate-700 hover:border-amber-500/50';

  return (
    <div
      className={`
        bg-slate-800 border rounded-lg transition-all duration-200
        hover:shadow-lg hover:shadow-sky-500/10
        ${borderColor}
      `}
    >
      {/* Header */}
      <div className="p-5 border-b border-slate-700">
        <div className="flex items-start gap-3">
          <div className={`p-2.5 ${typeInfo.bgColor} rounded-lg flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${typeInfo.color}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-white truncate mb-1">
              {datasource.name}
            </h3>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700 text-slate-300">
              {getTypeLabel(datasource.type)}
            </span>
          </div>
        </div>
      </div>

      {/* Body - Status & Metadata */}
      <div className="p-5 space-y-3">
        {/* Status Badge */}
        {getStatusBadge()}

        {/* Metadata Footer */}
        <div className="flex items-center gap-4 pt-2 border-t border-slate-700 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            <span>Actualizado {formatRelativeTime(datasource.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-4 bg-slate-900/50 border-t border-slate-700 flex items-center justify-end gap-2">
        <button
          onClick={() => onView(datasource)}
          className="
            flex items-center gap-2 px-4 py-2 text-sm font-medium
            text-sky-400 hover:bg-sky-500/10 rounded-lg
            transition-colors
          "
        >
          <Eye className="w-4 h-4" />
          Ver
        </button>

        {canManage && (
          <>
            <button
              onClick={() => onEdit(datasource)}
              className="
                flex items-center gap-2 px-4 py-2 text-sm font-medium
                bg-blue-600 hover:bg-blue-700 text-white rounded-lg
                transition-colors
              "
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>

            <button
              onClick={() => onAnalyze(datasource.id)}
              disabled={isAnalyzing}
              className="
                flex items-center gap-2 px-4 py-2 text-sm font-medium
                bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg
                transition-colors disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <RefreshCw className={`w-4 h-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
              Analizar
            </button>

            <button
              onClick={() => onDelete(datasource)}
              className="
                flex items-center gap-2 px-4 py-2 text-sm font-medium
                bg-red-600 hover:bg-red-700 text-white rounded-lg
                transition-colors
              "
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </>
        )}
      </div>
    </div>
  );
};
